/**
 * `<MarketDataApplyPanel>` — market-default-apply-ux Phase 2 Session 2.7 (DNE-7).
 *
 * Slimmed to a TEASER that references the turn-anchored chat receipt.
 * Removes the standing per-row dropdown picker (moved to the receipt's
 * informational/unmatched affordances per ADR 0003).
 *
 * Renders a compact entry-point card:
 *   <Chip variant="market"> + "Market data" + "{applied} applied · {pulled} also pulled"
 *   + "See the full apply receipt in the conversation ↑"
 *
 * On click → dispatches `arcanum:scroll-to-system-message` (the existing window-event
 * precedent, apps/web CLAUDE.md) to jump to the turn-anchored receipt, OR calls the
 * optional `onJumpToReceipt` callback if provided.
 *
 * Count source (EXACT — ADR 0008, parity with the receipt):
 *   `marketApplyCounts` is threaded from builder.tsx, computed from the SAME
 *   apply_market_defaults tool_result `<MarketDefaultApplyReceipt>` reads —
 *   applied = location-excluded applied[]+appliedTableCells[]; pulled =
 *   informationalSet+genuinelyUnmatchedSet. The teaser count therefore cannot
 *   drift from the receipt count. When `marketApplyCounts` is absent (e.g. a
 *   pre-feature tool_result), the panel falls back to the appliedMarketFields
 *   length + the cache-derived "also pulled" approximation.
 *
 * PRESERVE the read-only `return null` gate (`:readOnly || nothingToShow → null`).
 *
 * The teaser POINTS at the receipt — it is NOT a duplicate surface.
 */
import clsx from 'clsx';
import type { MarketDataCacheRow } from '@/lib/api';
import { Chip } from '@/components/ui/Chip';

/** Event name — mirrors the scroll-to-system-message precedent in web CLAUDE.md. */
const SCROLL_TO_SYSTEM_MESSAGE_EVENT = 'arcanum:scroll-to-system-message';

export interface MarketDataApplyPanelProps {
  /**
   * Fetched cache rows for the conversation's metro (from useApplyValue.rows).
   * Used to derive the "also pulled" count when the full partition isn't available.
   */
  rows: MarketDataCacheRow[];
  /**
   * Itemized list of fields successfully applied by the latest
   * `apply_market_defaults` call. Each entry carries `{ inputName, marketField, source }`.
   * This is shape-A (scalar writes) only — appliedTableCells[] count is NOT included
   * (see STOP+REPORT wiring gap note below).
   */
  appliedMarketFields?: Array<{
    inputName: string;
    marketField: string;
    source: string;
  }>;
  /**
   * market-default-apply-ux Phase 2 Session 2.7 — exact ADR-0008 partition
   * counts threaded from builder.tsx (the same apply_market_defaults tool_result
   * `<MarketDefaultApplyReceipt>` reads): `applied` = location-excluded
   * applied[]+appliedTableCells[]; `pulled` = informationalSet+genuinelyUnmatchedSet.
   * When present these are the authoritative counts (the teaser cannot drift from
   * the receipt). When ABSENT the panel falls back to the `appliedMarketFields`
   * length + the cache-derived "also pulled" approximation below.
   */
  marketApplyCounts?: { applied: number; pulled: number };
  /** Read-only mode (admin read-only Builder) — render nothing. */
  readOnly?: boolean;
  /**
   * Optional callback for the jump-to-receipt affordance. When provided,
   * called instead of (or in addition to) dispatching the window event.
   * Session 2.4 supplies this from builder.tsx once the receipt is wired.
   */
  onJumpToReceipt?: () => void;
  /**
   * The message/turn id of the latest apply receipt — threaded through to the
   * window event `detail.messageId` so ChatThread can scroll to and expand
   * the matching group. Optional — the event fires without it for back-compat.
   * Session 2.4 supplies this once the receipt-data walk is wired.
   */
  receiptMessageId?: string;
}

/**
 * Flatten `MarketDataCacheRow[]` → per-field entries, deduped by marketField.
 * INTERNAL — used only to count "also pulled" cache fields when the full
 * partition isn't available (wiring-gap fallback).
 */
function flattenCacheFieldCount(
  rows: MarketDataCacheRow[],
  appliedSet: Set<string>
): number {
  const seen = new Set<string>();
  let count = 0;
  for (const row of rows) {
    if (!row.data || typeof row.data !== 'object') continue;
    for (const [marketField, pv] of Object.entries(
      row.data as Record<string, unknown>
    )) {
      if (seen.has(marketField)) continue;
      if (
        pv !== null &&
        typeof pv === 'object' &&
        'value' in pv &&
        'provenance' in pv &&
        typeof (pv as { value: unknown }).value === 'number' &&
        Number.isFinite((pv as { value: number }).value)
      ) {
        seen.add(marketField);
        // Count only fields NOT already applied
        if (!appliedSet.has(marketField)) {
          count++;
        }
      }
    }
  }
  return count;
}

/**
 * Teaser entry-point card: references the turn-anchored chat receipt.
 * Renders null when readOnly or there is nothing to show (no applied + no pulled).
 */
export function MarketDataApplyPanel({
  rows,
  appliedMarketFields,
  marketApplyCounts,
  readOnly = false,
  onJumpToReceipt,
  receiptMessageId,
}: MarketDataApplyPanelProps) {
  // EXACT path (ADR 0008): when builder.tsx threads the partition counts from
  // the same apply_market_defaults tool_result the receipt reads, use them
  // verbatim so the teaser count never drifts from the receipt count.
  // FALLBACK path: derive an approximation from the appliedMarketFields prop +
  // the durable cache (used only if marketApplyCounts is absent, e.g. a
  // pre-feature tool_result that lacks the partition).
  const appliedSet = new Set(
    (appliedMarketFields ?? []).map((a) => a.marketField)
  );
  const applied =
    marketApplyCounts?.applied ?? (appliedMarketFields ?? []).length;
  const pulled =
    marketApplyCounts?.pulled ?? flattenCacheFieldCount(rows, appliedSet);

  // Preserve the read-only gate verbatim (research § 3 W-15 / R-RO).
  const nothingToShow = applied === 0 && pulled === 0;
  if (readOnly || nothingToShow) {
    return null;
  }

  function handleClick() {
    // Call the optional callback first (e.g. setComposerMode or scroll state).
    onJumpToReceipt?.();

    // Dispatch the `arcanum:scroll-to-system-message` window event so ChatThread
    // can scroll to and expand the matching turn group. Mirrors the existing
    // scroll-to-system-message precedent (web CLAUDE.md; EditSyncedToast.tsx;
    // ModelViewEditGroup.tsx). The messageId is optional — ChatThread handles
    // the undefined case by scrolling to the most-recent system message.
    if (typeof window !== 'undefined') {
      try {
        window.dispatchEvent(
          new CustomEvent(SCROLL_TO_SYSTEM_MESSAGE_EVENT, {
            detail: receiptMessageId ? { messageId: receiptMessageId } : {},
          })
        );
      } catch {
        // Defensive — CustomEvent unsupported envs shouldn't crash.
      }
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="market-data-apply-panel"
      className={clsx(
        'w-full text-left rounded-[3px] border border-panel-border bg-panel-bg',
        'hover:border-panel-border-hi transition-colors cursor-pointer px-3 py-2'
      )}
    >
      {/* Header row: chip + "Market data" label + arrow */}
      <div className="flex items-center justify-between mb-1">
        <span className="flex items-center gap-1.5">
          <Chip variant="market" label="market" />
          <span className="text-[8px] font-mono font-semibold tracking-[1.4px] uppercase text-text-muted">
            Market data
          </span>
        </span>
        <span
          className="text-text-dim text-[11px] font-mono"
          aria-hidden="true"
        >
          →
        </span>
      </div>

      {/* Counts: "{applied} applied · {pulled} also pulled" */}
      <div className="flex items-center gap-2 text-[10px] font-mono tabular-nums">
        <span className="text-accent">{applied} applied</span>
        <span className="text-text-dim">·</span>
        <span className="text-text-muted">{pulled} also pulled</span>
      </div>

      {/* Jump affordance */}
      <span className="mt-0.5 block text-[9px] font-body text-text-dim">
        See the full apply receipt in the conversation ↑
      </span>
    </button>
  );
}
